import { FC } from 'react';
import Modal, { useAsyncModal } from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import {
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Select,
	Typography,
} from 'antd';
import { SubmitMPPAssetInformation } from '@/libs/fleet-api/services/mpp';
import { useForm } from '@/libs/antd/form';
import rule from '@/libs/antd/rules';
import {
	useSelectAddress,
	useSelectBouwheer,
} from '@/libs/fleet-api/hooks/master-data';
import {
	AddressMasterData,
	BouwheerMasterData,
} from '@/libs/fleet-api/services/master-data';

export type AssetModalForm = SubmitMPPAssetInformation & {
	bowheer?: BouwheerMasterData;
	address?: AddressMasterData;
};

export type AssetModalParam =
	| {
			action: 'add';
	  }
	| {
			action: 'edit';
			record: AssetModalForm;
	  };

export const useAssetModal = () => {
	return useAsyncModal<AssetModalParam, AssetModalForm>();
};

export type AssetModalProps = ReturnType<typeof useAssetModal>;

const AssetModal: FC<AssetModalProps> = ({ modal, data, close }) => {
	const { form, fieldName, value } = useForm<AssetModalForm>();

	const selectBouwheer = useSelectBouwheer();
	const selectAddress = useSelectAddress();

	return (
		<Modal
			{...modal}
			width={940}
			closable={false}
			buttonPosition="end"
			cancelText="BACK"
			okText="SAVE"
			onOk={form.submit}
			onCancel={() => close()}
			afterOpenChange={(isOpen) => {
				if (isOpen && data?.action === 'edit') {
					form.setFieldsValue(data.record);
				}

				if (!isOpen) form.resetFields();
			}}
		>
			<div style={{ marginBottom: 20 }}>
				<FleetTitle>Add Informasi Aset</FleetTitle>
			</div>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				labelCol={{ span: 12 }}
				wrapperCol={{ span: 12 }}
				onFinish={close}
			>
				<Form.Item
					name={fieldName('address')}
					noStyle
				/>
				<Form.Item
					name={fieldName('bowheer')}
					noStyle
				/>
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('bpkbOwnerName')}
							label="Nama STNK/BPKB/Invoice"
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('unitAmount')}
							label="Jumlah Unit"
							rules={[rule.required()]}
						>
							<InputNumber />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('assetUser')}
							label="Pengguna Aset"
							rules={[rule.required()]}
						>
							<Input />
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('bowheerId')}
							label="Nama Bouwheer"
							rules={[rule.required()]}
						>
							<Select
								{...selectBouwheer}
								onSelect={(value, option) => {
									selectBouwheer.onSelect!(value, option);
									form.setFieldValue(fieldName('bowheer'), option.data);
								}}
								onClear={() => {
									form.setFieldValue(fieldName('bowheer'), null);
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Bouwheer Type">
							<Input
								disabled
								value={value?.bowheer?.type}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Industri Bouwheer">
							<Input
								disabled
								value={value?.bowheer?.industrySector?.sectorLevel5}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Typography.Title level={3}>Lokasi Penggunaan</Typography.Title>
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item
							name={fieldName('postalCodeId')}
							label="Kode Pos"
							getValueFromEvent={(_, option) => option.data.postalId}
							getValueProps={(value: AddressMasterData) => {
								return {
									value: value?.postalCode,
								};
							}}
						>
							<Select
								{...selectAddress}
								onSelect={(value, option) => {
									selectAddress.onSelect!(value, option);
									form.setFieldValue(fieldName('address'), option.data);
								}}
								onClear={() => {
									form.setFieldValue(fieldName('address'), null);
								}}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Kelurahan">
							<Input
								disabled
								value={value?.address?.locationName}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={40}>
					<Col span={12}>
						<Form.Item label="Kecamatan">
							<Input
								disabled
								value={value?.address?.districtName}
							/>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item label="Kota">
							<Input
								disabled
								value={value?.address?.cityName}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
			</Form>
		</Modal>
	);
};

export default AssetModal;
